import { createRouter, createWebHistory } from 'vue-router';
import AppLayout from '@/layout/AppLayout.vue';
import { getToken } from '../utils/auth';
import { usePermissions } from '@/composables/usePermissions';
import Login from '@/views/Login.vue';
import CompleteInformation from '@/views/CompleteInformation.vue';
import CustomerLayout from '@/layout/CustomerLayout.vue';
import TrackingOrder from '@/views/TrackingOrder.vue';

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/login',
      component: Login,
      meta: { requiresAuth: false }
    },
    {
      path: '/order-information/',
      component: CustomerLayout,
      meta: { requiresAuth: false },
      children: [
        {
          path: ':id',
          name: 'completeInformation',
          component: CompleteInformation,
          meta: { requiresAuth: false }
        }
      ]
    },
    {
      path: '/tracking-search',
      component: TrackingOrder,
      meta: { requiresAuth: false }
    },
    {
      path: '/quote/:token',
      name: 'PublicQuote',
      component: () => import('@/views/PublicQuote.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '/',
          name: 'dashboard',
          component: () => import('@/views/DashboardOverview.vue')
        },
        {
          path: '/countries',
          name: 'Countries',
          component: () => import('@/views/Country.vue')
        },
        {
          path: '/cities',
          name: 'Cities',
          component: () => import('@/views/City.vue')
        },
        {
          path: '/airlines',
          name: 'Airlines',
          component: () => import('@/views/Airline.vue')
        },
        {
          path: '/agency',
          name: 'Agency',
          component: () => import('@/views/Agency.vue')
        },
        {
          path: '/agents',
          name: 'Agents',
          component: () => import('@/views/Agents.vue')
        },
        {
          path: '/currencies',
          name: 'Currencies',
          component: () => import('@/views/Currency.vue')
        },
        {
          path: '/services',
          name: 'Services',
          component: () => import('@/views/Service.vue')
        },
        {
          path: '/package-items',
          name: 'PackageItems',
          component: () => import('@/views/PackageItem.vue')
        },
        {
          path: '/tracking-status',
          name: 'TrackingStatus',
          component: () => import('@/views/TrackingStatus.vue')
        },
        {
          path: '/payment-methods',
          name: 'PaymentMethods',
          component: () => import('@/views/PaymentMethod.vue')
        },
        {
          path: '/base-locations',
          name: 'BaseLocations',
          component: () => import('@/views/BaseLocation.vue')
        },
        {
          path: '/carrying-term',
          name: 'CarryingTerm',
          component: () => import('@/views/CarryingTerm.vue')
        },
        {
          path: '/references/:type',
          name: 'References',
          component: () => import('@/views/References.vue')
        },
        {
          path: '/non-console-references',
          name: 'NonConsoleReferences',
          component: () => import('@/views/NonConsoleReferences.vue')
        },
        {
          path: '/manifest-references',
          name: 'ManifestReferences',
          component: () => import('@/views/ReferenceManifest.vue')
        },
        {
          path: '/console-manifest',
          name: 'ConsoleManifest',
          component: () => import('@/views/ConsoleManifest.vue')
        },
        {
          path: '/references/:id/tracking',
          name: 'ReferencesTracking',
          component: () => import('@/views/ReferencesTracking.vue')
        },
        {
          path: '/references/:id/financial-items',
          name: 'ReferencesFinancial',
          component: () => import('@/views/ReferenceFinancial.vue')
        },
        {
          path: '/references/:id/transport-plan',
          name: 'ReferenceTransportPlan',
          component: () => import('@/views/TransportPlan.vue'),
          meta: { permissions: ['routes.view'] }
        },
        {
          path: '/references/:id/documents',
          name: 'ReferenceDocuments',
          component: () => import('@/views/ShipmentDocuments.vue'),
          meta: { permissions: ['documents.view'] }
        },
        {
          path: '/references/:id/shipment-operations',
          name: 'ReferenceShipmentOperations',
          component: () => import('@/views/ShipmentOperations.vue'),
          meta: { permissions: ['clearance.view', 'special_cargo.view'] }
        },
        {
          path: '/operations',
          name: 'OperationsWorkbench',
          component: () => import('@/views/OperationsWorkbench.vue'),
          meta: { permissions: ['operations.view'] }
        },
        {
          path: '/claims',
          name: 'Claims',
          component: () => import('@/views/Claims.vue'),
          meta: { permissions: ['claims.view'] }
        },
        {
          path: '/references/:id/print-reception',
          name: 'ReferencesPrintReception',
          component: () => import('@/views/ReferencesReceptionPrint.vue')
        },
        {
          path: '/references/:id/airway-bill',
          name: 'ReferencesPrintAirwayBill',
          component: () => import('@/views/ReferencesAirWayBillPrint.vue')
        },
        {
          path: '/invoices',
          name: 'Invoices',
          component: () => import('@/views/Invoices.vue')
        },
        {
          path: '/quotes',
          name: 'Quotes',
          component: () => import('@/views/Quotes.vue'),
          meta: { permissions: ['quotes.view'] }
        },
        {
          path: '/rate-cards',
          name: 'RateCards',
          component: () => import('@/views/RateCards.vue'),
          meta: { permissions: ['rates.view'] }
        },
        {
          path: '/invoices/:id',
          name: 'ShowInvoice',
          component: () => import('@/views/ShowInvoice.vue')
        },
        {
          path: '/bulk-tracking',
          name: 'BulkTrackingStore',
          component: () => import('@/views/ReferenceTrackingBulkAction.vue')
        },
        {
          path: '/pickup-list',
          name: 'PickupList',
          component: () => import('@/views/PickupList.vue'),
          meta: { permissions: ['dispatch.view', 'cod.view'] }
        },
        {
          path: '/confirmed-cod',
          name: 'ConfirmedCod',
          component: () => import('@/views/ConfirmedCod.vue'),
          meta: { permissions: ['cod.view', 'cod.settle'] }
        },
        {
          path: '/my-pickups',
          name: 'MyPickups',
          component: () => import('@/views/UserPickupList.vue'),
          meta: { permissions: ['dispatch.view'] }
        },
        {
          path: '/customers',
          name: 'CustomersList',
          component: () => import('@/views/CustomerList.vue')
        },
        {
          path: '/admins',
          name: 'AdminsList',
          component: () => import('@/views/Admins.vue')
        },
        {
          path: '/agents',
          name: 'AgentsList',
          component: () => import('@/views/Agents.vue')
        },
        {
          path: '/customer-center/:id',
          name: 'CustomersCenter',
          component: () => import('@/views/CustomerCenter.vue')
        },
        {
          path: '/reports',
          meta: { requiresAuth: true },
          children: [
            {
              path: '',
              name: 'ReportCenter',
              component: () => import('@/views/reports/ReportCenter.vue')
            },
            {
              path: '/reports/organizational',
              name: 'OrganizationalReports',
              component: () => import('@/views/reports/ReportCategory.vue'),
              meta: { reportCategory: 'organizational' }
            },
            {
              path: '/reports/charts',
              name: 'ChartReports',
              component: () => import('@/views/reports/ReportCategory.vue'),
              meta: { reportCategory: 'charts' }
            },
            {
              path: '/reports/useful',
              name: 'UsefulReports',
              component: () => import('@/views/reports/ReportCategory.vue'),
              meta: { reportCategory: 'useful' }
            },
            {
              path: '/sales-reports',
              name: 'SalesReport',
              component: () => import('@/views/SalesReport.vue')
            },
            {
              path: '/daily-report-traffic-income',
              name: 'DailyReportTrafficIncomeAverage',
              component: () => import('@/views/reports/DailyReportTrafficIncomeAverage.vue')
            },
            {
              path: '/date-report-traffic-income',
              name: 'DateReportTrafficIncomeAverage',
              component: () => import('@/views/reports/DateReportTrafficIncomeAverage.vue')
            },
            {
              path: '/undelivered-package-report',
              name: 'UndeliveredPackageReport',
              component: () => import('@/views/reports/UndeliveredPackageReport.vue')
            },
            {
              path: '/cod-service-report',
              name: 'CodServiceReport',
              component: () => import('@/views/reports/TrafficReportAndFareServiceRevenueAtTheDestination.vue')
            },
            {
              path: '/traffic-revenue-average-rate-reports',
              name: 'TrafficRevenueAverageRateReport',
              component: () => import('@/views/reports/ShipmentSeparationReport.vue')
            },
            {
              path: '/stock-in-customs',
              name: 'StockInCustoms',
              component: () => import('@/views/reports/StockInCustoms.vue')
            },
            {
              path: '/total-count-income-month',
              name: 'TrafficPerformanceReportAndDailyRevenuePerMonth',
              component: () => import('@/views/reports/TrafficPerformanceReportAndDailyRevenuePerMonth.vue')
            },
            {
              path: '/average-shipping-international-time',
              name: 'ShipmentTrendMatrixReportInternational',
              component: () => import('@/views/reports/ShipmentTrendMatrixReportInternational.vue')
            },
            {
              path: '/delivered-package-report',
              name: 'DeliverPackageReport',
              component: () => import('@/views/reports/DeliverPackageReport.vue')
            },
            {
              path: '/average-shipping-time',
              name: 'ShipmentTrendMatrixReport',
              component: () => import('@/views/reports/ShipmentTrendMatrixReport.vue')
            },
            {
              path: '/traffic-income-average-per-package',
              name: 'TrafficReportIncomeAndAverageRateByShipment',
              component: () => import('@/views/reports/TrafficReportIncomeAndAverageRateByShipment.vue')
            },
            {
              path: '/traffic-report-of-incoming-shipment-by-province',
              name: 'TrafficReportOfIncomingShipmentByProvince',
              component: () => import('@/views/reports/TrafficReportOfIncomingShipmentByProvince.vue')
            },
            {
              path: '/report-per-weight-and-package-type',
              name: 'ReportPerWeightAndPackageType',
              component: () => import('@/views/reports/ReportPerWeightAndPackageType.vue')
            },
            {
              path: '/report-revenue-from-suspended-counters',
              name: 'TrafficReportOfReServiceRevenueFromSuspendedCounters',
              component: () => import('@/views/reports/TrafficReportOfReServiceRevenueFromSuspendedCounters.vue')
            },
            {
              path: '/national-code-and-postal-code-report',
              name: 'PostalCodeStatisticsReportAndRecipientAndSenderNationalCode',
              component: () => import('@/views/reports/PostalCodeStatisticsReportAndRecipientAndSenderNationalCode.vue')
            },
            {
              path: '/expired-delivery-report',
              name: 'ShipmentReportWithExpirationDateOutsideTheCommitment',
              component: () => import('@/views/reports/ShipmentReportWithExpirationDateOutsideTheCommitment.vue')
            },
            {
              path: '/non-distribution-report',
              name: 'ReportAccordingToNonDistribution',
              component: () => import('@/views/reports/ReportAccordingToNonDistribution.vue')
            },
            {
              path: '/delivered-package-percent-report',
              name: 'DeliveredPackagePercentReport',
              component: () => import('@/views/reports/DeliveredPackagePercentReport.vue')
            },
            {
              path: '/reports/order-status',
              name: 'OrderStatusReport',
              component: () => import('@/views/reports/OrderStatusReport.vue')
            },
            {
              path: '/reports/revenue',
              name: 'RevenueReport',
              component: () => import('@/views/reports/RevenueReport.vue')
            },
            {
              path: '/reports/payment',
              name: 'PaymentReport',
              component: () => import('@/views/reports/PaymentReport.vue')
            },
            {
              path: '/reports/customer-performance',
              name: 'CustomerPerformanceReport',
              component: () => import('@/views/reports/CustomerPerformanceReport.vue')
            },
            {
              path: '/reports/popular-routes',
              name: 'PopularRoutesReport',
              component: () => import('@/views/reports/PopularRoutesReport.vue')
            },
            {
              path: '/reports/volume-weight',
              name: 'VolumeWeightReport',
              component: () => import('@/views/reports/VolumeWeightReport.vue')
            },
            {
              path: '/reports/pipeline-funnel',
              name: 'ShipmentPipelineFunnel',
              component: () => import('@/views/reports/ShipmentPipelineFunnel.vue')
            },
            {
              path: '/reports/revenue-trend',
              name: 'RevenueTrendReport',
              component: () => import('@/views/reports/RevenueTrendReport.vue')
            },
            {
              path: '/reports/route-performance',
              name: 'RoutePerformanceChart',
              component: () => import('@/views/reports/RoutePerformanceChart.vue')
            },
            {
              path: '/reports/customer-performance-chart',
              name: 'CustomerPerformanceChart',
              component: () => import('@/views/reports/CustomerPerformanceChart.vue')
            },
            {
              path: '/reports/flight',
              name: 'FlightReport',
              component: () => import('@/views/reports/FlightReport.vue')
            },
            {
              path: '/reports/special-cargo',
              name: 'SpecialCargoReport',
              component: () => import('@/views/reports/SpecialCargoReport.vue')
            },
            {
              path: '/reports/operations-dashboard',
              name: 'OperationsDashboard',
              component: () => import('@/views/reports/OperationsDashboard.vue')
            }
          ]
        },
        {
          path: '/pending-pickup-list',
          name: 'PendingPickupList',
          component: () => import('@/views/PendingPickupList.vue')
        },
        {
          path: '/uikit/formlayout',
          name: 'formlayout',
          component: () => import('@/views/uikit/FormLayout.vue')
        },
        {
          path: 'development',
          name: 'Development',
          component: () => import('@/views/Development.vue')
        },
        {
          path: '/uikit/input',
          name: 'input',
          component: () => import('@/views/uikit/Input.vue')
        },
        {
          path: '/uikit/floatlabel',
          name: 'floatlabel',
          component: () => import('@/views/uikit/FloatLabel.vue')
        },
        {
          path: '/uikit/invalidstate',
          name: 'invalidstate',
          component: () => import('@/views/uikit/InvalidState.vue')
        },
        {
          path: '/uikit/button',
          name: 'button',
          component: () => import('@/views/uikit/Button.vue')
        },
        {
          path: '/uikit/table',
          name: 'table',
          component: () => import('@/views/uikit/Table.vue')
        },
        {
          path: '/uikit/list',
          name: 'list',
          component: () => import('@/views/uikit/List.vue')
        },
        {
          path: '/notes',
          name: 'Notes',
          component: () => import('@/views/Notes.vue')
        },
        {
          path: '/settings',
          name: 'Settings',
          component: () => import('@/views/Settings.vue')
        },
        {
          path: '/system-health',
          name: 'SystemHealth',
          component: () => import('@/views/SystemHealth.vue')
        },
        {
          path: '/uikit/tree',
          name: 'tree',
          component: () => import('@/views/uikit/Tree.vue')
        },
        {
          path: '/uikit/panel',
          name: 'panel',
          component: () => import('@/views/uikit/Panels.vue')
        },

        {
          path: '/uikit/overlay',
          name: 'overlay',
          component: () => import('@/views/uikit/Overlay.vue')
        },
        {
          path: '/uikit/media',
          name: 'media',
          component: () => import('@/views/uikit/Media.vue')
        },
        {
          path: '/uikit/menu',
          component: () => import('@/views/uikit/Menu.vue'),
          children: [
            {
              path: '/uikit/menu',
              component: () => import('@/views/uikit/menu/PersonalDemo.vue')
            },
            {
              path: '/uikit/menu/seat',
              component: () => import('@/views/uikit/menu/SeatDemo.vue')
            },
            {
              path: '/uikit/menu/payment',
              component: () => import('@/views/uikit/menu/PaymentDemo.vue')
            },
            {
              path: '/uikit/menu/confirmation',
              component: () => import('@/views/uikit/menu/ConfirmationDemo.vue')
            }
          ]
        },
        {
          path: '/uikit/message',
          name: 'message',
          component: () => import('@/views/uikit/Messages.vue')
        },
        {
          path: '/uikit/file',
          name: 'file',
          component: () => import('@/views/uikit/File.vue')
        },
        {
          path: '/uikit/charts',
          name: 'charts',
          component: () => import('@/views/uikit/Chart.vue')
        },
        {
          path: '/uikit/misc',
          name: 'misc',
          component: () => import('@/views/uikit/Misc.vue')
        },
        {
          path: '/blocks',
          name: 'blocks',
          component: () => import('@/views/utilities/Blocks.vue')
        },
        {
          path: '/utilities/icons',
          name: 'icons',
          component: () => import('@/views/utilities/Icons.vue')
        },
        {
          path: '/pages/timeline',
          name: 'timeline',
          component: () => import('@/views/pages/Timeline.vue')
        },
        {
          path: '/pages/empty',
          name: 'empty',
          component: () => import('@/views/pages/Empty.vue')
        },
        {
          path: '/pages/crud',
          name: 'crud',
          component: () => import('@/views/pages/Crud.vue')
        },
        {
          path: '/guide',
          name: 'OperationsGuide',
          component: () => import('@/views/utilities/Documentation.vue')
        },
        {
          path: '/documentation',
          redirect: '/guide'
        }
      ]
    },
    {
      path: '/landing',
      name: 'landing',
      component: () => import('@/views/pages/Landing.vue')
    },
    {
      path: '/pages/notfound',
      name: 'notfound',
      component: () => import('@/views/pages/NotFound.vue')
    },
    {
      path: '/auth/access',
      name: 'accessDenied',
      component: () => import('@/views/pages/auth/Access.vue')
    },
    {
      path: '/auth/error',
      name: 'error',
      component: () => import('@/views/pages/auth/Error.vue')
    }
  ]
});

router.beforeEach((to, from, next) => {
  const token = getToken();
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!token) {
      next('/login');
    } else {
      const { canAny } = usePermissions();
      const protectedPaths = [
        { pattern: /^\/admins(?:\/|$)/, permissions: ['users.manage'] },
        { pattern: /^\/settings(?:\/|$)/, permissions: ['system.settings.manage'] },
        { pattern: /^\/quotes(?:\/|$)/, permissions: ['quotes.view'] },
        { pattern: /^\/rate-cards(?:\/|$)/, permissions: ['rates.view'] },
        { pattern: /^\/system-health(?:\/|$)/, permissions: ['system.health.view'] },
        { pattern: /^\/operations(?:\/|$)/, permissions: ['operations.view'] },
        { pattern: /^\/claims(?:\/|$)/, permissions: ['claims.view'] },
        { pattern: /^\/pickup-list(?:\/|$)/, permissions: ['dispatch.view', 'cod.view'] },
        { pattern: /^\/confirmed-cod(?:\/|$)/, permissions: ['cod.view', 'cod.settle'] },
        { pattern: /^\/my-pickups(?:\/|$)/, permissions: ['dispatch.view'] },
        {
          pattern: /^\/(console-manifest|non-console-references)(?:\/|$)/,
          permissions: ['manifests.view']
        },
        { pattern: /^\/references\/[^/]+\/documents(?:\/|$)/i, permissions: ['documents.view'] },
        {
          pattern: /^\/references\/[^/]+\/shipment-operations(?:\/|$)/i,
          permissions: ['clearance.view', 'special_cargo.view']
        },
        { pattern: /^\/Invoices(?:\/|$)/i, permissions: ['invoices.view'] },
        { pattern: /^\/sales-report(?:\/|$)/i, permissions: ['finance.profit.view'] },
        {
          pattern: /^\/reports\/(revenue|revenue-trend|route-performance)(?:\/|$)/,
          permissions: ['finance.profit.view']
        },
        { pattern: /^\/reports(?:\/|$)/, permissions: ['reports.view'] },
        { pattern: /^\/References(?:\/|$)/i, permissions: ['shipments.view'] }
      ];
      const rule = protectedPaths.find((item) => item.pattern.test(to.path));
      if (rule && !canAny(rule.permissions)) {
        next({ name: 'accessDenied' });
      } else {
        next();
      }
    }
  } else {
    next();
  }
});

export default router;
